import { useCallback, useMemo } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { PhoneIcon } from 'lucide-react';

import { useIsSidebarIAEnabled } from '@/app-shell/flags/heidi-work-umbrella';
import { useIsRibbonRail } from '@/app-shell/layout/Navigation/hooks/useIsRibbonRail';
import {
  useFooterRailCollapsible,
  type FooterMoreRowDescriptor,
} from '@/app-shell/layout/Navigation/sidebar/footer-rail/footer-rail-context';
import { FOOTER_COLLAPSE_EXIT_MS } from '@/app-shell/layout/Navigation/sidebar/footer-rail/footer-rail.constants';
import { useCommitAfterCollapseExit } from '@/app-shell/layout/Navigation/sidebar/footer-rail/use-commit-after-collapse-exit';
import {
  DISCOVERY_CALL_DEV_STUB_URL,
  discoveryCallDevForceShowAtom,
  isDiscoveryCallDevOverridesEnabled,
} from '@/atoms/discoveryCallDevAtoms';
import {
  allPrimaryItemsCollapsedAtom,
  discoveryCallCollapsedIntoPrimaryAtom,
  discoveryCallPrimaryEntryAtom,
} from '@/atoms/sideNavAtoms';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  getDiscoveryCallLinkForCountry,
  getLiveDemoLinkForCountry,
  getLiveDemoMaxAccountAgeDays,
} from '@/utils/country-config';
import { ButtonAction, ScreenName } from '@heidi/analytics/vocabulary';
import { SidebarMenu } from '@heidi/design-system/page-shell';
import { countries } from '@heidi/utils/countries';

import {
  navigationDiscoveryCall,
  navigationFranceCall,
  navigationGermanCall,
  navigationSpanishCall,
} from '@/lib/analytics/scribe/general';
import { NavItem } from '../sidebar';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const COUNTRY_CODE_BY_NAME: Record<string, string | undefined> = countries.reduce(
  (acc, c) => {
    acc[c.name] = c.countryCode;
    return acc;
  },
  {} as Record<string, string | undefined>
);

interface DiscoveryCallNavItemProps {
  onAfterClick?: () => void;
}

export function DiscoveryCallNavItem({ onAfterClick }: DiscoveryCallNavItemProps) {
  const intl = useIntl();
  const { data: user } = useQueryUser();

  const countryCode = user?.country ? COUNTRY_CODE_BY_NAME[user.country] : undefined;
  const rawLanguage = user?.language_time_settings?.display_language ?? undefined;
  // Normalise locale-formatted values (`de-CH`, `fr-FR`, `es-MX`) down to the
  // primary subtag — `getDiscoveryCallLinkForCountry` does exact key matching
  // against the language map, and the legacy analytics branch checks `'de' |
  // 'fr' | 'es'` literals.
  const language = (rawLanguage ?? '').toLowerCase().split('-')[0] || undefined;

  // Double-gated to non-production so a manually-set sessionStorage value can
  // never surface this CTA in prod.
  const devForceShow =
    useAtomValue(discoveryCallDevForceShowAtom) && isDiscoveryCallDevOverridesEnabled();

  const resolvedDiscoveryCallUrl = useMemo(
    () => getDiscoveryCallLinkForCountry(countryCode, language),
    [countryCode, language]
  );
  const discoveryCallUrl = devForceShow
    ? DISCOVERY_CALL_DEV_STUB_URL
    : resolvedDiscoveryCallUrl;
  const liveDemoUrl = useMemo(
    () => getLiveDemoLinkForCountry(countryCode, language),
    [countryCode, language]
  );

  const liveDemoMaxAccountAgeDays = getLiveDemoMaxAccountAgeDays(countryCode);
  // When a window is configured and `created_at` is unavailable, treat the
  // user as outside it — the live-demo CTA is a new-user offer, so
  // eligibility must be provable.
  const isWithinLiveDemoWindow =
    liveDemoMaxAccountAgeDays === null ||
    (!!user?.created_at &&
      Date.now() - new Date(user.created_at).getTime() <
        liveDemoMaxAccountAgeDays * MS_PER_DAY);

  // One slot, one CTA: the live-demo variant takes the slot when its link
  // resolves and the account is inside the window; otherwise the slot falls back
  // to the discovery-call CTA. A user never sees both. The dev force-show targets
  // the discovery-call CTA specifically, so the live-demo branch is skipped then.
  const showLiveDemo = !devForceShow && Boolean(liveDemoUrl) && isWithinLiveDemoWindow;
  const showDiscoveryCall = !showLiveDemo && Boolean(discoveryCallUrl);
  const visible = showLiveDemo || showDiscoveryCall;

  const handleLiveDemoClick = useCallback(() => {
    // Same routing as `trackButtonClicked` (Snowplow + Datadog only), via
    // the sanctioned `track()` entry point — new `dispatch.analyticsScribe.*`
    // call sites are frozen by the containment ratchet.
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: ScreenName.Sidenav,
        action: ButtonAction.SidebarLiveDemoCtaClicked,
        label: 'sidebar_live_demo_cta',
      },
      options: { amplitude: false },
    });
    onAfterClick?.();
  }, [onAfterClick]);

  const handleDiscoveryCallClick = useCallback(() => {
    navigationDiscoveryCall();
    // Fire legacy language-keyed events in parallel so existing dashboards
    // don't go dark while they're migrated onto `navigationDiscoveryCall`.
    // Removed in the follow-up cleanup PR alongside the deprecated flags.
    if (language === 'de') {
      navigationGermanCall();
    } else if (language === 'fr') {
      navigationFranceCall();
    } else if (language === 'es') {
      navigationSpanishCall();
    }
    onAfterClick?.();
  }, [language, onAfterClick]);

  const liveDemoLabel = intl.formatMessage({
    defaultMessage: 'Book a live 1:1 demo',
    id: '++IwXJpaa6',
    description:
      'Sidebar CTA linking to the HubSpot booking page for a live one-on-one demo',
  });
  const discoveryCallLabel = intl.formatMessage({
    defaultMessage: 'Book your discovery call',
    id: '+pyW6feqm8',
    description: 'Sidebar CTA linking to the discovery-call booking page',
  });

  const isRibbonRail = useIsRibbonRail();
  const isSidebarIAEnabled = useIsSidebarIAEnabled();
  const allPrimaryCollapsed = useAtomValue(allPrimaryItemsCollapsedAtom);

  // Footer overflow: Discovery collapses into the footer More in ribbon icon
  // mode (IA off), or into the primary nav More after all primary items have
  // folded (IA on, cascade position 1). Both variants share the `discovery` slot.
  const descriptor = useMemo<FooterMoreRowDescriptor>(
    () =>
      showLiveDemo
        ? {
            id: 'discovery',
            label: liveDemoLabel,
            icon: <PhoneIcon />,
            testId: 'sidebar-link-live-demo',
            href: liveDemoUrl ?? undefined,
            external: true,
            onSelect: handleLiveDemoClick,
          }
        : {
            id: 'discovery',
            label: discoveryCallLabel,
            icon: <PhoneIcon />,
            testId: 'sidebar-link-discovery-call',
            href: discoveryCallUrl ?? undefined,
            external: true,
            onSelect: handleDiscoveryCallClick,
          },
    [
      showLiveDemo,
      liveDemoLabel,
      liveDemoUrl,
      discoveryCallLabel,
      discoveryCallUrl,
      handleLiveDemoClick,
      handleDiscoveryCallClick,
    ]
  );
  const isCollapsed = useFooterRailCollapsible({
    id: 'discovery',
    // IA off: collapse into footer More as before.
    // IA on: only enter the budget after all primary items have folded (cascade
    // position 1 — Discovery Call folds before Dictate, Help, Notifications).
    visible: visible && isRibbonRail && (!isSidebarIAEnabled || allPrimaryCollapsed),
    pinned: false,
    descriptor,
  });

  const setDiscoveryCallCollapsedIntoPrimary = useSetAtom(
    discoveryCallCollapsedIntoPrimaryAtom
  );
  // Treat "not visible" as "already done" in the cascade so users who have no
  // discovery-call/live-demo URL don't block Dictate from collapsing.
  useCommitAfterCollapseExit(
    isSidebarIAEnabled && (isCollapsed || !visible),
    setDiscoveryCallCollapsedIntoPrimary,
    FOOTER_COLLAPSE_EXIT_MS
  );

  // Keep the primary More descriptor up to date so useCollapsedFooterDescriptors
  // can render the correct label/href without duplicating resolution logic.
  const setDiscoveryCallPrimaryEntry = useSetAtom(discoveryCallPrimaryEntryAtom);
  useEffect(() => {
    if (!visible) {
      setDiscoveryCallPrimaryEntry(null);
      return;
    }
    setDiscoveryCallPrimaryEntry({
      href: showLiveDemo ? (liveDemoUrl ?? undefined) : (discoveryCallUrl ?? undefined),
      label: showLiveDemo ? liveDemoLabel : discoveryCallLabel,
      onClick: showLiveDemo ? handleLiveDemoClick : handleDiscoveryCallClick,
      testId: showLiveDemo ? 'sidebar-link-live-demo' : 'sidebar-link-discovery-call',
    });
  }, [
    visible,
    showLiveDemo,
    liveDemoUrl,
    discoveryCallUrl,
    liveDemoLabel,
    discoveryCallLabel,
    handleLiveDemoClick,
    handleDiscoveryCallClick,
    setDiscoveryCallPrimaryEntry,
  ]);

  if (!visible || isCollapsed) return null;

  const item = showLiveDemo ? (
    <NavItem
      label={liveDemoLabel}
      icon={<PhoneIcon />}
      href={liveDemoUrl ?? undefined}
      external
      variant={isRibbonRail ? undefined : 'emphasis'}
      layout={isRibbonRail ? 'iconOnly' : 'default'}
      onClick={handleLiveDemoClick}
      testId="sidebar-link-live-demo"
    />
  ) : (
    <NavItem
      label={discoveryCallLabel}
      icon={<PhoneIcon />}
      href={discoveryCallUrl ?? undefined}
      external
      variant={isRibbonRail ? undefined : 'emphasis'}
      layout={isRibbonRail ? 'iconOnly' : 'default'}
      onClick={handleDiscoveryCallClick}
      testId="sidebar-link-discovery-call"
    />
  );

  // Ribbon rail: bare item — the shared footer <ul> is owned by SideNavFooter.
  return isRibbonRail ? item : <SidebarMenu>{item}</SidebarMenu>;
}
